import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtUSD, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TrendingUp } from "lucide-react";
import WaterfallVisualizer from "@/components/WaterfallVisualizer";
import { useAuth } from "@/context/AuthContext";

/**
 * Records a sale for a parcel or a jewelry, with live waterfall preview.
 * props: target_type ("parcel" | "jewelry"), target_id, existingSale (nullable), onChanged, onDeleted
 */
export default function SaleActionPanel({ target_type, target_id, existingSale, onChanged }) {
    const { canWrite } = useAuth();
    const [form, setForm] = useState({ sale_price: "", sale_date: new Date().toISOString().slice(0, 10), buyer: "", notes: "", received_by: "bashari" });
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [buyers, setBuyers] = useState([]);
    const [editingRecipient, setEditingRecipient] = useState(false);
    const [savingRecipient, setSavingRecipient] = useState(false);

    useEffect(() => {
        if (existingSale || !canWrite) return;
        api.get("/sales/buyers").then(r => setBuyers(r.data?.buyers || [])).catch(() => {});
    }, [existingSale, canWrite]);

    useEffect(() => {
        if (existingSale) return;
        const price = Number(form.sale_price);
        if (!price || price <= 0) { setPreview(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/sales/preview", { target_type, target_id, sale_price: price, sale_date: form.sale_date });
                setPreview(data);
            } catch { /* silent */ }
        }, 250);
        return () => clearTimeout(t);
    }, [form.sale_price, form.sale_date, target_type, target_id, existingSale]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/sales", {
                target_type, target_id,
                sale_price: Number(form.sale_price),
                sale_date: form.sale_date,
                received_by: form.received_by,
                buyer: form.buyer || null,
                notes: form.notes || null,
            });
            toast.success("Sale recorded — waterfall executed");
            onChanged?.();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const revert = async () => {
        if (!confirm("Delete this sale? Item will return to previous status.")) return;
        try {
            await api.delete(`/sales/${existingSale.id}`);
            toast.success("Sale reverted");
            onChanged?.();
        } catch { toast.error("Failed"); }
    };

    const updateRecipient = async (partner) => {
        setSavingRecipient(true);
        try {
            await api.patch(`/sales/${existingSale.id}/received-by`, { received_by: partner });
            toast.success(`Recipient set to ${partner === "bashari" ? "Bashari" : "Ido"}`);
            setEditingRecipient(false);
            onChanged?.();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSavingRecipient(false); }
    };

    const [editingBuyer, setEditingBuyer] = useState(false);
    const [buyerDraft, setBuyerDraft] = useState("");
    const [savingBuyer, setSavingBuyer] = useState(false);
    const [existingBuyers, setExistingBuyers] = useState([]);

    useEffect(() => {
        if (!existingSale || !canWrite) return;
        api.get("/sales/buyers").then(r => setExistingBuyers(r.data?.buyers || [])).catch(() => { });
    }, [existingSale, canWrite]);

    const saveBuyer = async () => {
        setSavingBuyer(true);
        try {
            const trimmed = buyerDraft.trim();
            await api.patch(`/sales/${existingSale.id}/buyer`, { buyer: trimmed || null });
            toast.success(trimmed ? "Buyer updated" : "Buyer cleared");
            setEditingBuyer(false);
            onChanged?.();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSavingBuyer(false); }
    };

    if (existingSale) {
        const rb = existingSale.received_by;
        const rbLabel = rb === "bashari" ? "Bashari" : rb === "ido" ? "Ido" : "—";
        return (
            <div className="space-y-6" data-testid="sale-recorded-panel">
                <div className="border border-emerald-500/40 bg-emerald-50 p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1 space-y-2">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700">Sold</div>
                        <div className="text-stone-700 text-sm">
                            {fmtDate(existingSale.sale_date)} · {fmtUSD(existingSale.sale_price)}
                        </div>
                        {/* Buyer editor */}
                        <div className="text-xs text-stone-600 flex items-center gap-2 flex-wrap" data-testid="sale-buyer">
                            <span className="text-[10px] uppercase tracking-widest text-stone-500">Buyer</span>
                            {!editingBuyer ? (
                                <>
                                    <span className="font-medium text-stone-900" data-testid="sale-buyer-value">{existingSale.buyer || "—"}</span>
                                    {canWrite && (
                                        <button
                                            type="button"
                                            onClick={() => { setBuyerDraft(existingSale.buyer || ""); setEditingBuyer(true); }}
                                            className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-emerald-700"
                                            data-testid="edit-buyer-button"
                                        >
                                            Change
                                        </button>
                                    )}
                                </>
                            ) : (
                                <span className="inline-flex items-center gap-2">
                                    <input
                                        autoFocus
                                        list="sale-panel-buyer-options"
                                        value={buyerDraft}
                                        onChange={e => setBuyerDraft(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") { e.preventDefault(); saveBuyer(); }
                                            if (e.key === "Escape") { setEditingBuyer(false); }
                                        }}
                                        placeholder="Buyer name"
                                        className="rounded-none border border-stone-300 bg-white px-2 py-1 text-xs w-44 focus:outline-none focus:border-emerald-600"
                                        data-testid="sale-buyer-input"
                                    />
                                    <datalist id="sale-panel-buyer-options">
                                        {existingBuyers.map(b => <option key={b} value={b} />)}
                                    </datalist>
                                    <button type="button" disabled={savingBuyer} onClick={saveBuyer} className="px-2 py-1 text-[10px] uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40" data-testid="save-buyer-button">Save</button>
                                    <button type="button" onClick={() => setEditingBuyer(false)} className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-800">Cancel</button>
                                </span>
                            )}
                        </div>
                        {/* Received-by editor */}
                        <div className="text-xs text-stone-600 flex items-center gap-2 flex-wrap" data-testid="sale-received-by">
                            <span className="text-[10px] uppercase tracking-widest text-stone-500">Payment received by</span>
                            {!editingRecipient ? (
                                <>
                                    <span className="font-medium text-stone-900" data-testid="sale-received-by-value">{rbLabel}</span>
                                    {canWrite && (
                                        <button
                                            type="button"
                                            onClick={() => setEditingRecipient(true)}
                                            className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-emerald-700"
                                            data-testid="edit-received-by-button"
                                        >
                                            Change
                                        </button>
                                    )}
                                </>
                            ) : (
                                <span className="inline-flex items-center gap-2">
                                    {["bashari", "ido"].map(p => (
                                        <button
                                            key={p}
                                            type="button"
                                            disabled={savingRecipient}
                                            onClick={() => updateRecipient(p)}
                                            className={`px-2.5 py-1 text-[11px] uppercase tracking-widest border ${rb === p ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300 bg-white text-stone-700 hover:border-emerald-600 hover:text-emerald-700"} disabled:opacity-40`}
                                            data-testid={`received-by-option-${p}`}
                                        >
                                            {p === "bashari" ? "Bashari" : "Ido"}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setEditingRecipient(false)}
                                        className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-800"
                                    >
                                        Cancel
                                    </button>
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={revert} className="text-xs uppercase tracking-widest text-stone-500 hover:text-rose-600 disabled:opacity-30 disabled:hover:text-stone-500 shrink-0" data-testid="revert-sale-button" disabled={!canWrite}>
                        Revert sale
                    </button>
                </div>
                <div className="border border-stone-200 bg-[#f5efe0] p-6">
                    <WaterfallVisualizer breakdown={existingSale.breakdown} />
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {canWrite ? (
                <form onSubmit={submit} className="border border-stone-200 bg-white p-5 space-y-3" data-testid="record-sale-form">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
                        <div className="text-xs uppercase tracking-widest text-stone-500">Record sale</div>
                    </div>
                    <Field label="Sale price (USD)">
                        <Input required type="number" step="0.01" min="0" value={form.sale_price} onChange={e => setForm({ ...form, sale_price: e.target.value })} className="rounded-none bg-[#faf7ef] border-stone-200" data-testid="sale-price-input" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Sale date">
                            <Input required type="date" value={form.sale_date} onChange={e => setForm({ ...form, sale_date: e.target.value })} className="rounded-none bg-[#faf7ef] border-stone-200" data-testid="sale-date-input" />
                        </Field>
                        <Field label="Buyer">
                            <Input
                                value={form.buyer}
                                onChange={e => setForm({ ...form, buyer: e.target.value })}
                                className="rounded-none bg-[#faf7ef] border-stone-200"
                                data-testid="sale-buyer-input"
                                list="known-buyers"
                                placeholder="Start typing a name…"
                                autoComplete="off"
                            />
                            <datalist id="known-buyers">
                                {buyers.map(b => <option key={b} value={b} />)}
                            </datalist>
                        </Field>
                    </div>
                    <Field label="Notes">
                        <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-[#faf7ef] border-stone-200" data-testid="sale-notes-input" />
                    </Field>
                    <Field label="Payment received by">
                        <div className="grid grid-cols-2 gap-2" data-testid="sale-received-by-selector">
                            {["bashari", "ido"].map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setForm({ ...form, received_by: p })}
                                    className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${form.received_by === p ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300 bg-white text-stone-700 hover:border-emerald-600 hover:text-emerald-700"}`}
                                    data-testid={`sale-received-by-${p}`}
                                >
                                    {p === "bashari" ? "Bashari" : "Ido"}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <Button type="submit" disabled={saving || !form.sale_price} className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="record-sale-submit">
                        {saving ? "Recording…" : "Record sale & execute waterfall"}
                    </Button>
                </form>
            ) : (
                <div className="border border-stone-200 bg-white p-5 text-sm text-stone-500" data-testid="record-sale-viewer-locked">
                    Read-only access. Only a partner (Bashari or Ido) can record a sale.
                </div>
            )}

            <div className="border border-stone-200 bg-[#f5efe0] p-6">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Waterfall preview</div>
                {preview ? <WaterfallVisualizer breakdown={preview} /> : (
                    <div className="text-sm text-stone-500">Enter a sale price to preview the payout waterfall.</div>
                )}
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div><Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">{label}</Label>{children}</div>
);
